import React, { useContext, useState, useEffect, useCallback } from "react";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router";
import { useQuery, useMutation } from "@apollo/client";
import { v4 } from "uuid";
import moment from "moment";

import CSLS from "../../../../tools/cs_local_storage";
import { CSAuth } from "../../../../tools/authentication";
import AppSettingsContext from "../../../context/AppSettingsContext";
import { TOKEN_REFRESH } from "../../../../queries/system/auth";

import { Button, Card, Dimmer, Grid, Icon, Table } from "tabler-react";

import { capitalize } from "../../../../tools/string_tools";
import ShopClassesScheduleBase from "./ShopClassesScheduleBase";
import ShopClassesScheduleFilter from "./ShopClassesScheduleFilter";
import ShopClassesScheduleButtonBook from "./ShopClassesScheduleButtonBook";
import ClassCapacityDisplay from "./components/ClassCapacityDisplay";

import { GET_CLASSES_QUERY } from "../../../schedule/classes/queries";
import { GET_SHOP_FEATURES_QUERY } from "../../../schedule/classes/all/waitlist/queries";
import { get_list_query_variables } from "./tools";

// Set some initial values for dates, if not found
if (!localStorage.getItem(CSLS.SHOP_CLASSES_DATE_FROM)) {
    console.log("date from not found... defaulting to today...");
    localStorage.setItem(
        CSLS.SHOP_CLASSES_DATE_FROM,
        moment().format("YYYY-MM-DD"),
    );
    localStorage.setItem(
        CSLS.SHOP_CLASSES_DATE_UNTIL,
        moment().add(6, "days").format("YYYY-MM-DD"),
    );
} else {
    const date_from = moment(localStorage.getItem(CSLS.SHOP_CLASSES_DATE_FROM));
    if (date_from.isBefore(moment(), "day")) {
        localStorage.setItem(
            CSLS.SHOP_CLASSES_DATE_FROM,
            moment().format("YYYY-MM-DD"),
        );
        localStorage.setItem(
            CSLS.SHOP_CLASSES_DATE_UNTIL,
            moment().add(6, "days").format("YYYY-MM-DD"),
        );
    }
}

function ShopClassesSchedule({ t, match, history }) {
    const appSettings = useContext(AppSettingsContext);
    const timeFormat = appSettings.timeFormatMoment;
    const [isTokenReady, setIsTokenReady] = useState(false);
    const [doTokenRefresh] = useMutation(TOKEN_REFRESH);

    const title = t("shop.home.title");

    // Check and refresh token if needed
    // Token check and refresh function
    const checkAndRefreshToken = useCallback(async () => {
        const tokenExp = localStorage.getItem(CSLS.AUTH_TOKEN_EXP);
        const refreshTokenExp = localStorage.getItem(
            CSLS.AUTH_REFRESH_TOKEN_EXP,
        );

        const isTokenExpired = !tokenExp || new Date() / 1000 >= tokenExp;
        const isRefreshTokenExpired =
            !refreshTokenExp || new Date() / 1000 >= refreshTokenExp;

        // If refresh token is expired, clean up and continue as anonymous
        if (isRefreshTokenExpired) {
            if (tokenExp || refreshTokenExp) {
                console.log(
                    "Refresh token expired, continuing as anonymous user",
                );
                CSAuth.cleanup();
            }
            return true; // Ready to proceed
        }

        // If auth token is still valid, no need to refresh
        if (!isTokenExpired) {
            console.log(
                "Auth token is still valid, continuing as authenticated user",
            );
            return true; // Ready to proceed
        }

        // At this point: auth token expired but refresh token is valid
        // Check if already refreshing
        if (window.refreshingToken) {
            // Wait for the ongoing refresh to complete
            await new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    if (!window.refreshingToken) {
                        clearInterval(checkInterval);
                        resolve();
                    }
                }, 100);
            });
            return true;
        }

        // Perform token refresh
        window.refreshingToken = true;
        try {
            console.log("Refreshing expired auth token before query");
            const { data } = await doTokenRefresh();
            CSAuth.updateTokenInfo(data.refreshToken);
            CSAuth.clearShopClassesCache();
            console.log("Token refresh successful");
        } catch (error) {
            console.error("Token refresh failed:", error);
            // Clean up auth state when refresh fails
            CSAuth.cleanup();
        } finally {
            window.refreshingToken = false;
        }

        return true; // Always proceed
    }, [doTokenRefresh]);

    // Initial token check on mount
    useEffect(() => {
        checkAndRefreshToken().then(() => setIsTokenReady(true));
    }, [checkAndRefreshToken]);

    // Skip the query until token is ready
    const {
        loading,
        error,
        data,
        refetch: originalRefetch,
    } = useQuery(GET_CLASSES_QUERY, {
        variables: get_list_query_variables(),
        notifyOnNetworkStatusChange: true,
        skip: !isTokenReady, // Skip query until token is ready
    });

    // Wrapped refetch that checks token first
    const refetch = useCallback(
        async (variables) => {
            console.log("Refetch requested, checking token first...");
            await checkAndRefreshToken();

            // Add a small delay to ensure cookies are updated
            await new Promise((resolve) => setTimeout(resolve, 100));

            return originalRefetch(variables);
        },
        [checkAndRefreshToken, originalRefetch],
    );

    // Check if waitlist feature is enabled
    const { data: shopFeaturesData } = useQuery(GET_SHOP_FEATURES_QUERY);
    const waitlistEnabled =
        shopFeaturesData?.systemFeatureShop?.waitlist || false;

    console.log(loading);

    const pageHeaderOptions = (
        <Button.List className="schedule-list-page-options-btn-list">
            <Button
                icon="chevron-left"
                color="secondary"
                disabled={moment(
                    localStorage.getItem(CSLS.SHOP_CLASSES_DATE_FROM),
                )
                    .subtract(7, "days")
                    .isBefore(moment(), "day")}
                onClick={() => {
                    let prevWeekFrom = moment(
                        localStorage.getItem(CSLS.SHOP_CLASSES_DATE_FROM),
                    ).subtract(7, "days");
                    let prevWeekUntil = moment(prevWeekFrom).add(6, "days");

                    localStorage.setItem(
                        CSLS.SHOP_CLASSES_DATE_FROM,
                        prevWeekFrom.format("YYYY-MM-DD"),
                    );
                    localStorage.setItem(
                        CSLS.SHOP_CLASSES_DATE_UNTIL,
                        prevWeekUntil.format("YYYY-MM-DD"),
                    );

                    refetch(get_list_query_variables());
                }}
            />
            <Button
                color="secondary"
                onClick={() => {
                    let currentWeekFrom = moment();
                    let currentWeekUntil = moment(currentWeekFrom).add(
                        6,
                        "days",
                    );

                    localStorage.setItem(
                        CSLS.SHOP_CLASSES_DATE_FROM,
                        currentWeekFrom.format("YYYY-MM-DD"),
                    );
                    localStorage.setItem(
                        CSLS.SHOP_CLASSES_DATE_UNTIL,
                        currentWeekUntil.format("YYYY-MM-DD"),
                    );

                    refetch(get_list_query_variables());
                }}
            >
                {t("general.today")}
            </Button>
            <Button
                icon="chevron-right"
                color="secondary"
                onClick={() => {
                    let nextWeekFrom = moment(
                        localStorage.getItem(CSLS.SHOP_CLASSES_DATE_FROM),
                    ).add(7, "days");
                    let nextWeekUntil = moment(nextWeekFrom).add(6, "days");

                    localStorage.setItem(
                        CSLS.SHOP_CLASSES_DATE_FROM,
                        nextWeekFrom.format("YYYY-MM-DD"),
                    );
                    localStorage.setItem(
                        CSLS.SHOP_CLASSES_DATE_UNTIL,
                        nextWeekUntil.format("YYYY-MM-DD"),
                    );

                    refetch(get_list_query_variables());
                }}
            />
        </Button.List>
    );

    if (!isTokenReady || loading)
        return (
            <ShopClassesScheduleBase
                title={title}
                pageHeaderOptions={pageHeaderOptions}
            >
                <br />
                <br />
                <br />
                <br />
                <Dimmer active={true} loader={true} />
            </ShopClassesScheduleBase>
        );
    if (error)
        return (
            <ShopClassesScheduleBase
                title={title}
                pageHeaderOptions={pageHeaderOptions}
            >
                {t("shop.classes.error_loading")}
            </ShopClassesScheduleBase>
        );

    return (
        <ShopClassesScheduleBase
            title={title}
            pageHeaderOptions={pageHeaderOptions}
        >
            <ShopClassesScheduleFilter data={data} refetch={refetch} />
            {data.scheduleClasses.map(({ date, bookingOpenOn, classes }) => (
                <Grid.Row key={v4()}>
                    <Grid.Col md={12}>
                        <Card>
                            <Card.Header>
                                <Card.Title>
                                    <b>
                                        {capitalize(
                                            moment(date).format("dddd"),
                                        )}
                                    </b>{" "}
                                    <span className="text-muted">
                                        {moment(date).format("LL")}
                                    </span>
                                </Card.Title>
                            </Card.Header>
                            {!classes.length ? (
                                <Card.Body>
                                    <p>{t("schedule.classes.empty_list")}</p>
                                </Card.Body>
                            ) : (
                                <Table cards>
                                    <Table.Body>
                                        {classes.map(
                                            ({
                                                scheduleItemId,
                                                frequencyType,
                                                date,
                                                status,
                                                holiday,
                                                holidayName,
                                                description,
                                                account,
                                                role,
                                                account2,
                                                role2,
                                                organizationLocationRoom,
                                                organizationClasstype,
                                                organizationLevel,
                                                timeStart,
                                                timeEnd,
                                                spaces,
                                                countAttendingAndBooked,
                                                availableSpacesOnline,
                                                waitlistEnabled:
                                                    classWaitlistEnabled,
                                                waitlistCount,
                                                displayPublic,
                                                bookingStatus,
                                                userWaitlistPosition,
                                                isUserOnWaitlist,
                                                canJoinWaitlist,
                                                userWaitlistAttendanceId,
                                                isBookable,
                                                userBookingStatus,
                                                userAttendanceId,
                                            }) => (
                                                <Table.Row>
                                                    <Table.Col>
                                                        <h5>
                                                            {
                                                                organizationClasstype.name
                                                            }{" "}
                                                            {moment(
                                                                date +
                                                                    " " +
                                                                    timeStart,
                                                            ).format(
                                                                timeFormat,
                                                            )}{" "}
                                                            {" - "}
                                                            {moment(
                                                                date +
                                                                    " " +
                                                                    timeEnd,
                                                            ).format(
                                                                timeFormat,
                                                            )}{" "}
                                                        </h5>
                                                        <span className="mr-2">
                                                            {account
                                                                ? account.fullName
                                                                : ""}
                                                        </span>
                                                        <span className="text-muted">
                                                            {organizationLevel &&
                                                                organizationLevel.name}
                                                        </span>
                                                        <br />
                                                        <span className="text-muted">
                                                            {
                                                                organizationLocationRoom
                                                                    .organizationLocation
                                                                    .name
                                                            }
                                                        </span>
                                                    </Table.Col>
                                                    <Table.Col>
                                                        <ShopClassesScheduleButtonBook
                                                            scheduleItemId={
                                                                scheduleItemId
                                                            }
                                                            classDate={date}
                                                            bookingOpenOn={
                                                                bookingOpenOn
                                                            }
                                                            bookingStatus={
                                                                bookingStatus
                                                            }
                                                            scheduleClass={{
                                                                waitlistEnabled:
                                                                    classWaitlistEnabled,
                                                                waitlistCount,
                                                                userWaitlistPosition,
                                                                isUserOnWaitlist,
                                                                canJoinWaitlist,
                                                                userWaitlistAttendanceId,
                                                                isBookable,
                                                                userBookingStatus,
                                                                userAttendanceId,
                                                            }}
                                                            waitlistEnabled={
                                                                waitlistEnabled
                                                            }
                                                        />
                                                        {bookingStatus !==
                                                        "FINISHED" ? (
                                                            <React.Fragment>
                                                                <div
                                                                    style={{
                                                                        clear: "both",
                                                                    }}
                                                                />
                                                                <div className="cs-shop-classes-schedule-business-indicator mt-2">
                                                                    <div className="float-right">
                                                                        <small>
                                                                            <Icon
                                                                                name="users"
                                                                                className="text-muted"
                                                                            />
                                                                        </small>
                                                                    </div>
                                                                    <ClassCapacityDisplay
                                                                        scheduleClass={{
                                                                            spaces,
                                                                            countAttendingAndBooked,
                                                                            waitlistCount,
                                                                            waitlistEnabled:
                                                                                classWaitlistEnabled,
                                                                        }}
                                                                        waitlistEnabled={
                                                                            waitlistEnabled
                                                                        }
                                                                    />
                                                                </div>
                                                            </React.Fragment>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </Table.Col>
                                                </Table.Row>
                                            ),
                                        )}
                                    </Table.Body>
                                </Table>
                            )}
                        </Card>
                    </Grid.Col>
                </Grid.Row>
            ))}
        </ShopClassesScheduleBase>
    );
}

export default withTranslation()(withRouter(ShopClassesSchedule));
